import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import { Button, Grid, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CategoryCreateSchema from './CategoryCreateSchema'

const TypographyError = styled(Typography)({
  color: '#d32f2f',
  paddingLeft: 14,
})

const CategoryCreate = () => {
  const { t } = useTranslation()
  const [disabled, setDisabled] = useState(false)

  const initialValues = {
    name: '',
    thumbnail: [],
    background: [],
  }

  const {
    register,
    trigger,
    control,
    handleSubmit,
    getValues,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(CategoryCreateSchema),
  })

  Inertia.on('finish', () => setDisabled(false))

  const onSubmit = (data) => {
    setDisabled(true)
    Inertia.post(route('admin.categories.store'), data, {
      onError: (errors) => {
        setError('name', { message: t(errors.name) })
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Section title={t('CATEGORIES.BASIC_INFORMATION')}>
            <Grid item lg={7} md={12} sm={12} xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <LabelField required title={t('CATEGORIES.NAME')}>
                    <TextField
                      fullWidth
                      id="name"
                      name="name"
                      size="small"
                      variant="outlined"
                      {...register('name')}
                      error={errors.name ? true : false}
                      helperText={errors.name?.message}
                      onFocus={() => trigger('name')}
                    />
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('CATEGORIES.IMAGES')}>
            <Grid item lg={7} md={12} sm={12} xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelField required recommend={t('VOUCHER.SLIDER_RECOMMEND')} title={t('CATEGORIES.THUMBNAIL')}>
                    <Controller
                      control={control}
                      name="thumbnail"
                      render={({ field }) => (
                        <InputUpload
                          {...field}
                          buttonName={t('CAMPAIGN.UPLOAD_SLIDER')}
                          clearErrors={clearErrors}
                          handleImageChange={(files) => setValue('thumbnail', files)}
                          id="thumbnail"
                          name="thumbnail"
                          ref={field.ref}
                          setError={setError}
                          title={t('CAMPAIGN.UPLOAD_SLIDER')}
                          value={getValues('thumbnail')}
                        />
                      )}
                    />
                    {errors.thumbnail && (
                      <TypographyError variant="caption">{errors.thumbnail?.message}</TypographyError>
                    )}
                  </LabelField>
                </Grid>

                <Grid item xs={12}>
                  <LabelField required recommend={t('VOUCHER.SLIDER_RECOMMEND')} title={t('CATEGORIES.BACKGROUND')}>
                    <Controller
                      control={control}
                      name="background"
                      render={({ field }) => (
                        <InputUpload
                          {...field}
                          buttonName={t('CAMPAIGN.UPLOAD_SLIDER')}
                          clearErrors={clearErrors}
                          handleImageChange={(files) => setValue('background', files)}
                          id="background"
                          name="background"
                          setError={setError}
                          title={t('CAMPAIGN.UPLOAD_SLIDER')}
                          value={getValues('background')}
                        />
                      )}
                    />
                    {errors.background && (
                      <TypographyError variant="caption">{errors.background?.message}</TypographyError>
                    )}
                  </LabelField>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs="auto">
          <Button color="primary" disabled={disabled} type="submit" variant="contained">
            {t('BUTTON.SAVE')}
          </Button>
        </Grid>

        <Grid item xs="auto">
          <Button color="inherit" component={InertiaLink} href={route('admin.categories.index')} variant="contained">
            {t('BUTTON.CANCEL')}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default CategoryCreate
